'use client';

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Sliders, ChevronRight, FileText, CheckCircle2, AlertTriangle, ShieldCheck } from 'lucide-react';
import { useForensicsTracker } from '../../lib/useForensicsTracker';
import { PremiumBackground } from '../effects/PremiumBackground';

export interface ValuationMatrixProps {
  id?: string;
}

export function ValuationMatrix({ id = 'valuation-matrix' }: ValuationMatrixProps = {}) {
  const { recordSliderChange } = useForensicsTracker();
  
  const [ebitda, setEbitda] = useState<number>(30);
  const [netDebt, setNetDebt] = useState<number>(10);
  const [scenario, setScenario] = useState<1 | 2 | 3>(3);

  // Trigger tracker on change
  useEffect(() => {
    recordSliderChange('valuation_ebitda', ebitda);
  }, [ebitda, recordSliderChange]);

  useEffect(() => {
    recordSliderChange('valuation_debt', netDebt);
  }, [netDebt, recordSliderChange]);

  // Calculations
  const format = (n: number) => Math.max(0, Math.round(n)).toLocaleString('ru-RU');

  const getCalculations = (tier: 1 | 2 | 3) => {
    let mult = tier === 1 ? 4.0 : tier === 2 ? 5.5 : 7.0;
    let base = ebitda * mult;
    let discount = tier === 1 ? base * 0.3 : 0; // 30% penalty for toxic
    let ev = base - discount;
    let preTax = ev - netDebt;
    // Taxes: Tier 3 gets 0% due to structuring (e.g. 284.2 NK RF), others get 15% standard
    let tax = tier === 3 ? 0 : preTax * 0.15; 
    let cash = preTax - tax;

    return { mult, base, discount, preTax, tax, cash };
  };

  const currentCalc = getCalculations(scenario);

  const ReceiptLine = ({ label, value, isBold = false, isGold = false, isNegative = false, isHighlight = false }: any) => (
    <div className={`flex items-end justify-between font-mono text-[11px] sm:text-xs mt-3 ${isBold ? 'font-semibold' : 'font-normal'} ${isGold ? 'text-[#8C6D37]' : isHighlight ? 'text-[#1E3A8A]' : 'text-[#0F172A]'}`}>
      <span className={isNegative ? 'text-[#DC2626]' : (isBold || isHighlight || isGold) ? '' : 'text-[#64748B]'}>{label}</span>
      <div className="flex-1 border-b border-dotted border-[#CBD5E1] mx-4 mb-1 opacity-60"></div>
      <span className={isNegative ? 'text-[#DC2626]' : ''}>{isNegative && value !== '0' ? '-' : ''}{value} ₽</span>
    </div>
  );

  return (
    <section 
      id={id}
      className="relative w-full py-24 md:py-32 bg-[#F8FAFC] text-[#0F172A] border-t border-b border-[#E2E8F0] overflow-hidden select-text"
    >
      <PremiumBackground variant={8} />

      <div className="relative z-10 max-w-[1200px] mx-auto px-4 sm:px-6 md:px-8 lg:px-12 flex flex-col gap-16 md:gap-20">
        
        {/* Header Section */}
        <div className="flex flex-col gap-5 max-w-3xl">
          <div className="luxury-eyebrow text-[#8C6D37] font-semibold flex items-center gap-2 tracking-[0.2em]">
            <Sliders size={12} className="text-[#8C6D37]" />
            АНАЛИТИЧЕСКИЙ ТЕРМИНАЛ
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-[46px] font-serif font-normal tracking-[-0.02em] leading-[1.1] text-[#0F172A] select-none">
            Почему одни клиники продаются за <span className="italic underline decoration-[#1E3A8A]/30 underline-offset-8">4.0x</span>, <br className="hidden sm:inline" />
            а другие — за <span className="text-[#1E3A8A] font-medium">7.0x</span>?
          </h2>
          <p className="text-[15px] sm:text-[16px] text-[#475569] leading-[1.65] font-sans font-light max-w-2xl mt-2">
            Разница в оценке — это не торг. Это математика, прозрачность активов и юридическая чистота. 
            Используйте панель ниже, чтобы увидеть, как структура сделки влияет на реальные деньги, которые вы получите на руки.
          </p>
        </div>

        {/* Matrix Container */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          
          {/* Left Column: Logic & Inputs (7 cols) */}
          <div className="lg:col-span-7 flex flex-col gap-10">
            
            {/* Sliders Area */}
            <div className="bg-[#FFFFFF] border border-[#E2E8F0] p-6 sm:p-8 rounded-sm shadow-sm flex flex-col gap-8">
              
              {/* EBITDA Slider */}
              <div className="flex flex-col gap-4">
                <div className="flex justify-between items-end">
                  <label htmlFor="ebitda-slider" className="font-mono text-xs text-[#64748B] uppercase tracking-wider font-semibold">Операционная прибыль (EBITDA)</label>
                  <span className="font-mono text-xl text-[#0F172A] tnum font-medium">{format(ebitda)} <span className="text-sm text-[#64748B]">млн ₽</span></span>
                </div>
                <input 
                  id="ebitda-slider"
                  type="range" 
                  min="10" 
                  max="300" 
                  step="5"
                  value={ebitda} 
                  onChange={(e) => setEbitda(Number(e.target.value))}
                  className="w-full appearance-none bg-transparent [&::-webkit-slider-runnable-track]:h-[2px] [&::-webkit-slider-runnable-track]:bg-[#E2E8F0] [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-1.5 [&::-webkit-slider-thumb]:bg-[#1E3A8A] [&::-webkit-slider-thumb]:-mt-[7px] [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:shadow-[0_0_0_2px_#FFF] focus:outline-none"
                />
              </div>

              {/* Net Debt Slider */}
              <div className="flex flex-col gap-4">
                <div className="flex justify-between items-end">
                  <label htmlFor="net-debt-slider" className="font-mono text-xs text-[#64748B] uppercase tracking-wider font-semibold">Чистый долг (Net Debt)</label>
                  <span className="font-mono text-xl text-[#0F172A] tnum font-medium">{format(netDebt)} <span className="text-sm text-[#64748B]">млн ₽</span></span>
                </div>
                <input 
                  id="net-debt-slider"
                  type="range" 
                  min="0" 
                  max="150" 
                  step="1"
                  value={netDebt} 
                  onChange={(e) => setNetDebt(Number(e.target.value))}
                  className="w-full appearance-none bg-transparent [&::-webkit-slider-runnable-track]:h-[2px] [&::-webkit-slider-runnable-track]:bg-[#E2E8F0] [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-1.5 [&::-webkit-slider-thumb]:bg-[#1E3A8A] [&::-webkit-slider-thumb]:-mt-[7px] [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:shadow-[0_0_0_2px_#FFF] focus:outline-none"
                />
              </div>
            </div>

            {/* Scenario Selectors */}
            <div className="flex flex-col gap-3">
              <h3 className="font-mono text-xs text-[#64748B] uppercase tracking-wider font-semibold mb-2">Сценарии оценки актива</h3>
              
              <button 
                onClick={() => setScenario(1)}
                className={`text-left p-4 rounded-sm border transition-all duration-200 flex gap-4 items-center ${scenario === 1 ? 'bg-[#FFFFFF] border-[#1E3A8A] shadow-md' : 'bg-[#FFFFFF]/50 border-[#E2E8F0] hover:bg-[#FFFFFF] hover:border-[#CBD5E1]'}`}
              >
                <div className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${scenario === 1 ? 'bg-[#1E3A8A]/10 text-[#1E3A8A]' : 'bg-[#F1F5F9] text-[#64748B]'}`}>
                  <AlertTriangle size={14} />
                </div>
                <div className="flex-1">
                  <div className="flex justify-between items-center">
                    <span className={`font-mono text-xs font-bold ${scenario === 1 ? 'text-[#1E3A8A]' : 'text-[#475569]'}`}>ТОКСИЧНЫЙ АКТИВ (4.0x)</span>
                  </div>
                  <p className="text-[11px] text-[#64748B] mt-1 font-sans">Серые схемы, кассовые разрывы, налоговые риски. Покупатель дисконтирует актив на 30%.</p>
                </div>
              </button>

              <button 
                onClick={() => setScenario(2)}
                className={`text-left p-4 rounded-sm border transition-all duration-200 flex gap-4 items-center ${scenario === 2 ? 'bg-[#FFFFFF] border-[#1E3A8A] shadow-md' : 'bg-[#FFFFFF]/50 border-[#E2E8F0] hover:bg-[#FFFFFF] hover:border-[#CBD5E1]'}`}
              >
                <div className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${scenario === 2 ? 'bg-[#1E3A8A]/10 text-[#1E3A8A]' : 'bg-[#F1F5F9] text-[#64748B]'}`}>
                  <FileText size={14} />
                </div>
                <div className="flex-1">
                  <div className="flex justify-between items-center">
                    <span className={`font-mono text-xs font-bold ${scenario === 2 ? 'text-[#1E3A8A]' : 'text-[#475569]'}`}>РЫНОЧНЫЙ СТАНДАРТ (5.5x)</span>
                  </div>
                  <p className="text-[11px] text-[#64748B] mt-1 font-sans">Обычная клиника без явных проблем, но без подготовки к M&A. Стандартные налоги 15%.</p>
                </div>
              </button>

              <button 
                onClick={() => setScenario(3)}
                className={`text-left p-4 rounded-sm border transition-all duration-200 flex gap-4 items-center ${scenario === 3 ? 'bg-[#FFFFFF] border-[#D4AF37] shadow-md ring-1 ring-[#D4AF37]/20' : 'bg-[#FFFFFF]/50 border-[#E2E8F0] hover:bg-[#FFFFFF] hover:border-[#CBD5E1]'}`}
              >
                <div className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${scenario === 3 ? 'bg-[#D4AF37]/10 text-[#8C6D37]' : 'bg-[#F1F5F9] text-[#64748B]'}`}>
                  <ShieldCheck size={14} />
                </div>
                <div className="flex-1">
                  <div className="flex justify-between items-center">
                    <span className={`font-mono text-xs font-bold ${scenario === 3 ? 'text-[#8C6D37]' : 'text-[#475569]'}`}>STRUCTURE PARTNERS (7.0x)</span>
                  </div>
                  <p className="text-[11px] text-[#64748B] mt-1 font-sans">Идеальная белая структура. Премия за прозрачность. Оптимизация налогов (ст. 284.2 НК РФ).</p>
                </div>
              </button>

            </div>

          </div>

          {/* Right Column: Receipt / Output (5 cols) */}
          <div className="lg:col-span-5 relative">
            
            {/* The Receipt Card */}
            <motion.div 
              key={scenario}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="bg-[#FFFFFF] rounded-sm border border-[#E2E8F0] shadow-[0_20px_60px_-15px_rgba(0,0,0,0.08)] p-8 md:p-10 relative overflow-hidden"
            >
              {/* Subtle top accent line */}
              <div className={`absolute top-0 left-0 w-full h-1 ${scenario === 3 ? 'bg-[#D4AF37]' : scenario === 2 ? 'bg-[#1E3A8A]' : 'bg-[#94A3B8]'}`} />

              <div className="flex justify-between items-start mb-10">
                <div className="flex flex-col gap-1">
                  <span className="font-mono text-[10px] text-[#64748B] uppercase tracking-widest">Оценка актива</span>
                  <span className={`font-mono text-xs font-bold ${scenario === 3 ? 'text-[#8C6D37]' : 'text-[#0F172A]'}`}>
                    СЦЕНАРИЙ {scenario === 3 ? 'ПРЕМИУМ' : scenario === 2 ? 'РЫНОК' : 'РИСК'}
                  </span>
                </div>
                <span className="font-mono text-[10px] text-[#64748B] border border-[#E2E8F0] px-2 py-1 rounded bg-[#F8FAFC]">
                  MULT: {currentCalc.mult.toFixed(1)}x
                </span>
              </div>

              {/* Big Value */}
              <div className="flex flex-col mb-12">
                <span className="text-[11px] text-[#64748B] font-mono mb-2">Гросс Оценка (Enterprise Value)</span>
                <div className="font-serif text-[42px] sm:text-[54px] font-normal tracking-[-0.03em] leading-none text-[#0F172A] tnum">
                  {format(currentCalc.base)} <span className="text-xl sm:text-2xl text-[#64748B] font-sans tracking-normal">млн</span>
                </div>
              </div>

              {/* The Waterfall Receipt */}
              <div className="flex flex-col gap-2">
                <ReceiptLine label={`EBITDA (${format(ebitda)} × ${currentCalc.mult.toFixed(1)})`} value={format(currentCalc.base)} />
                
                {scenario === 1 && (
                  <ReceiptLine label="Дисконт за риски (30%)" value={format(currentCalc.discount)} isNegative={true} />
                )}

                <ReceiptLine label="Вычет чистого долга" value={format(netDebt)} isNegative={true} />
                
                <div className="my-2 border-b border-[#E2E8F0]"></div>
                
                <ReceiptLine label="База до налогов" value={format(currentCalc.preTax)} isBold={true} />
                
                <ReceiptLine 
                  label={scenario === 3 ? "Налоги (ст. 284.2 НК РФ)" : "Налоги (15%)"} 
                  value={format(currentCalc.tax)} 
                  isNegative={scenario !== 3} 
                  isHighlight={scenario === 3}
                />

                <div className="mt-6 pt-6 border-t-2 border-[#0F172A]">
                  <div className="flex items-end justify-between font-mono text-sm sm:text-[15px] font-bold text-[#0F172A]">
                    <span>CASH-IN (НА РУКИ)</span>
                    <div className="flex-1 border-b border-dotted border-[#CBD5E1] mx-4 mb-1 opacity-60"></div>
                    <span className="text-[#8C6D37] text-lg sm:text-xl">{format(currentCalc.cash)} ₽</span>
                  </div>
                </div>

              </div>

              {/* Context Note */}
              <div className="mt-8 bg-[#F8FAFC] border border-[#E2E8F0] p-4 rounded-sm flex items-start gap-3">
                <CheckCircle2 size={16} className={scenario === 3 ? "text-[#8C6D37] shrink-0 mt-0.5" : "text-[#64748B] shrink-0 mt-0.5"} />
                <p className="text-[11px] text-[#475569] font-sans leading-relaxed">
                  {scenario === 3 
                    ? "Структурирование сделки силами Structure Partners позволяет получить максимальный мультипликатор и легально обнулить налог на продажу бизнеса." 
                    : scenario === 2 
                    ? "Стандартная рыночная практика. Покупатель не видит оснований для премии, а вы платите полный налог с продажи."
                    : "Из-за кассовых разрывов и непрозрачной структуры покупатель срезает оценку на треть, чтобы защитить свои риски."}
                </p>
              </div>

            </motion.div>

          </div>

        </div>
      </div>
    </section>
  );
}
